import {
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Close, Receipt, Search, Star, WhatsApp } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import { obterTextoPedido } from "../../../services/avaliacaoDelivery"
import { AvaliacaoDeliveryActionsTypes } from "../context/avaliacaoDeliveryActions"
import { useAvaliacaoDeliveryContext } from "../context/useAvaliacaoDeliveryContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1000px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    statCard: {
        borderRadius: 15,
        padding: theme.spacing(2),
        textAlign: "center",
        flex: 1,
        minWidth: 120,
    },
    statValue: {
        fontWeight: 700,
        fontSize: "1.8rem",
        color: "#2c3e50",
    },
    statLabel: {
        color: "#7f8c8d",
        fontSize: "0.85rem",
    },
    notaChip: {
        fontWeight: 700,
        minWidth: 40,
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    comentarioCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 200,
    },
    pedidoText: {
        whiteSpace: "pre-wrap",
        fontFamily: "monospace",
        fontSize: "0.85rem",
        color: "#2c3e50",
    },
}))

function msParaInputDate(ms: number): string {
    const d = new Date(ms)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
}

function inputDateParaMs(value: string): number {
    return new Date(value + "T00:00:00").getTime()
}

function notaCor(nota: number | null): string {
    if (nota == null) return "#7f8c8d"
    if (nota >= 9) return "#4caf50"
    if (nota >= 7) return "#ffc107"
    return "#f44336"
}

function gerarLinkWhatsapp(telefone: string | null, nomeCliente: string): string {
    if (!telefone) return "#"
    let tel = "55" + telefone
    tel = tel.replace(/[\s\-().+]/g, "")
    const texto = encodeURIComponent(
        `Olá ${nomeCliente}! Obrigado pela avaliação do seu último pedido conosco.`
    )
    return `https://api.whatsapp.com/send?phone=${tel}&text=${texto}`
}

export function ListaAvaliacoes() {
    const classes = useStyles()
    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, pesquisar, erroCarregamento } = useAvaliacaoDeliveryContext()
    const { enqueueSnackbar } = useSnackbar()

    const [textoPedido, setTextoPedido] = useState<string | null>(null)
    const [dialogAberto, setDialogAberto] = useState(false)
    const [carregandoPedido, setCarregandoPedido] = useState(false)
    const [termoBusca, setTermoBusca] = useState("")
    const [inicioStr, setInicioStr] = useState(() => msParaInputDate(dados.inicioMs))
    const [fimStr, setFimStr] = useState(() => msParaInputDate(dados.fimMs))

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    function isDataValida(str: string): boolean {
        if (!str || str.length !== 10) return false
        const d = new Date(str + "T00:00:00")
        if (isNaN(d.getTime())) return false
        const [yyyy, mm, dd] = str.split("-").map(Number)
        return d.getFullYear() === yyyy && d.getMonth() + 1 === mm && d.getDate() === dd
    }

    const handlePesquisar = async () => {
        if (!isDataValida(inicioStr)) {
            alertStart("Data de início inválida.", "error")
            return
        }
        if (!isDataValida(fimStr)) {
            alertStart("Data de fim inválida.", "error")
            return
        }
        const inicioMs = inputDateParaMs(inicioStr)
        const fimMs = inputDateParaMs(fimStr)
        dispatch({ type: AvaliacaoDeliveryActionsTypes.ATUALIZAR_INICIO, payload: inicioMs })
        dispatch({ type: AvaliacaoDeliveryActionsTypes.ATUALIZAR_FIM, payload: fimMs })
        await pesquisar(inicioMs, fimMs)
    }

    const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

    const listaFiltrada = termoBusca
        ? dados.avaliacoes.filter(
              a =>
                  normalizar(a.clienteNome).includes(normalizar(termoBusca)) ||
                  (a.comentario && normalizar(a.comentario).includes(normalizar(termoBusca)))
          )
        : dados.avaliacoes

    const abrirPedido = async (idPedido: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setCarregandoPedido(true)
        setDialogAberto(true)
        const res = await obterTextoPedido(autorizacao, idPedido)
        setCarregandoPedido(false)
        if (typeof res === "string") {
            setTextoPedido(res)
        } else {
            alertStart(res.mensagem || "Erro ao carregar pedido", "error")
            setDialogAberto(false)
        }
    }

    const fecharDialog = () => {
        setDialogAberto(false)
        setTextoPedido(null)
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                {/* Título */}
                <Box mb={3}>
                    <Typography variant={isSmall ? "h5" : "h4"} className={classes.titleText}>
                        Avaliações do Delivery
                    </Typography>
                    <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                        Veja como seus clientes estão avaliando os pedidos.
                    </Typography>
                </Box>

                {/* Stats */}
                <Box display="flex" flexWrap="wrap" style={{ gap: 16 }} mb={3}>
                    <Paper className={classes.statCard} elevation={2}>
                        <Typography className={classes.statValue}>{dados.total}</Typography>
                        <Typography className={classes.statLabel}>Total de avaliações</Typography>
                    </Paper>
                    <Paper className={classes.statCard} elevation={2}>
                        <Typography className={classes.statValue}>
                            {dados.media != null ? dados.media.toFixed(1) : "—"}
                        </Typography>
                        <Typography className={classes.statLabel}>Média de nota</Typography>
                    </Paper>
                    <Paper className={classes.statCard} elevation={2}>
                        <Typography className={classes.statValue}>
                            {dados.nps != null ? dados.nps : "—"}
                        </Typography>
                        <Typography className={classes.statLabel}>NPS</Typography>
                    </Paper>
                </Box>

                {/* Filtros + Tabela */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Star className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Lista de Avaliações
                        </Typography>

                        <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginLeft: "auto" }}>
                            <TextField
                                label="Data início"
                                type="date"
                                size="small"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                value={inicioStr}
                                onChange={e => setInicioStr(e.target.value)}
                            />
                            <TextField
                                label="Data fim"
                                type="date"
                                size="small"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                value={fimStr}
                                onChange={e => setFimStr(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                onClick={handlePesquisar}
                                style={{
                                    backgroundColor: "#4caf50",
                                    color: "white",
                                    borderRadius: 25,
                                    boxShadow: "0 2px 8px rgba(76,175,80,0.4)",
                                }}
                                startIcon={<Search />}
                            >
                                Filtrar
                            </Button>
                        </Box>

                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Buscar cliente ou comentário..."
                            value={termoBusca}
                            onChange={e => setTermoBusca(e.target.value)}
                            style={{ minWidth: 240 }}
                        />
                    </Box>

                    <Box className={classes.cardContent}>
                        {listaFiltrada.length === 0 ? (
                            <Typography
                                style={{ color: "#7f8c8d", textAlign: "center", padding: 32 }}
                            >
                                Nenhuma avaliação encontrada.
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 560, overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ minWidth: 740, width: "100%" }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 60 }} />
                                            <TableCell style={{ minWidth: 160 }}>Cliente</TableCell>
                                            <TableCell style={{ minWidth: 130 }}>Data</TableCell>
                                            <TableCell style={{ minWidth: 200 }}>
                                                Comentário
                                            </TableCell>
                                            <TableCell style={{ minWidth: 90 }} align="center">
                                                Nota
                                            </TableCell>
                                            <TableCell style={{ minWidth: 100 }} align="center">
                                                Ações
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listaFiltrada.map(avaliacao => (
                                            <TableRow key={avaliacao.id} hover>
                                                <TableCell>
                                                    {avaliacao.facebookId ? (
                                                        <Avatar
                                                            src={`https://graph.facebook.com/${avaliacao.facebookId}/picture?type=square`}
                                                            style={{ width: 36, height: 36 }}
                                                        />
                                                    ) : (
                                                        <Avatar
                                                            src="https://s3-sa-east-1.amazonaws.com/clientefielsp/aplicacao/semfoto.png"
                                                            style={{ width: 36, height: 36 }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={avaliacao.clienteNome}
                                                >
                                                    {avaliacao.clienteNome}
                                                </TableCell>
                                                <TableCell>
                                                    {avaliacao.dataHora != null
                                                        ? new Date(
                                                              avaliacao.dataHora
                                                          ).toLocaleString("pt-BR", {
                                                              day: "2-digit",
                                                              month: "2-digit",
                                                              year: "numeric",
                                                              hour: "2-digit",
                                                              minute: "2-digit",
                                                          })
                                                        : "—"}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.comentarioCell}
                                                    title={avaliacao.comentario ?? ""}
                                                >
                                                    {avaliacao.comentario || "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={avaliacao.nota ?? "—"}
                                                        className={classes.notaChip}
                                                        style={{
                                                            backgroundColor: notaCor(
                                                                avaliacao.nota
                                                            ),
                                                            color: "white",
                                                        }}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        title="Ver pedido"
                                                        onClick={() =>
                                                            abrirPedido(avaliacao.idPedido)
                                                        }
                                                        style={{ color: "#1976d2" }}
                                                    >
                                                        <Receipt />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        title="WhatsApp"
                                                        component="a"
                                                        href={gerarLinkWhatsapp(
                                                            avaliacao.telefoneCliente,
                                                            avaliacao.clienteNome
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: "#25d366" }}
                                                    >
                                                        <WhatsApp />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>

                {/* Dialog pedido */}
                <Dialog open={dialogAberto} onClose={fecharDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Detalhes do Pedido
                            </Typography>
                            <IconButton size="small" onClick={fecharDialog}>
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {carregandoPedido ? (
                            <Typography style={{ color: "#7f8c8d" }}>Carregando...</Typography>
                        ) : (
                            <Typography className={classes.pedidoText}>{textoPedido}</Typography>
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </Box>
    )
}
